import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, ChevronLeft, ChevronRight, Trash2, Plus, CheckCircle2, AlertTriangle, GripVertical, Scissors, Hammer, Lock, Check, ShoppingCart, Undo2 } from "lucide-react";
import { api, errMsg, fmt } from "../lib/api";
import { useData } from "../lib/DataContext";
import UndoBatchDialog from "../components/UndoBatchDialog";
import { PageHeader, Panel } from "../components/shared";
import CapacityDeficit from "../components/CapacityDeficit";
import Schedule90Button from "../components/Schedule90Button";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BORDER = { ok: "border-[#10B981]", short: "border-[#EF4444]", empty: "border-[#1E293B]", completed: "border-[#334155] opacity-70" };
const DOT = { ok: "bg-[#10B981]", short: "bg-[#EF4444]", empty: "bg-[#334155]", completed: "bg-[#6B7280]" };
// Complete Day button hidden for now; set true to restore it (handler + backend endpoint still active)
const SHOW_COMPLETE_DAY = false;

const GATE = {
  ready: { cls: "bg-[#10B981]/20 text-[#10B981]", label: "READY FOR ASSEMBLY" },
  pending: { cls: "bg-[#F59E0B]/20 text-[#F59E0B]", label: "GATED: REQUIRES T2 PREP" },
  blocked: { cls: "bg-[#EF4444]/20 text-[#EF4444]", label: "GATED: T2 NOT SCHEDULED" },
};

function BatchCard({ b, onMove, onDelete, onDragStart, onComplete, onUndo, canLeft, canRight, busy }) {
  const g = b.gate && !b.completed ? GATE[b.gate] : null;
  const canRun = !b.completed && (!b.gate || b.gate === "ready");
  const [draft, setDraft] = useState(String(b.qty));
  useEffect(() => { setDraft(String(b.qty)); }, [b.qty]);
  const runQty = Math.max(1, Math.round(Number(draft) || 0));
  const short = runQty < b.qty;
  // The box is this run's count only: the scheduled goal (b.qty) never changes here; above it = overproduce, below it = partial registration
  const setRun = (n) => { setDraft(String(Math.max(1, Math.round(n)))); };
  const commit = () => setRun(Number(draft) || 0);
  return (
    <div draggable={!b.completed} onDragStart={onDragStart} data-testid={`batch-${b.sku}-${b.id}`}
      className={`p-2.5 rounded-md border ${b.completed ? "bg-[#10B981]/5 border-[#10B981]/30" : b.tier === 2 ? "bg-[#312E81]/25 border-[#6366F1]/40" : "bg-[#111827] border-[#1E293B]"} ${b.completed ? "" : "cursor-grab active:cursor-grabbing"}`}>
      <div className="flex items-start gap-1.5">
        {!b.completed && <GripVertical className="w-4 h-4 text-[#6B7280] mt-0.5 shrink-0" />}
        <div className="text-sm font-semibold leading-tight flex-1 min-w-0">{b.name}</div>
        {b.tier === 2 && b.stage && <span data-testid={`batch-stage-${b.id}`} className="font-mono text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#A78BFA]/20 text-[#C4B5FD] shrink-0">S{b.stage}</span>}
      </div>
      {b.reason === "plan" && <div data-testid={`batch-plan-${b.id}`} className="mt-1.5 mr-1 inline-flex items-center gap-1 font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-[#10B981]/20 text-[#10B981]">PRODUCTION PLAN{b.need_by ? ` · DUE ${b.need_by.slice(5)}` : ""}</div>}
      {g && <div data-testid={`batch-gate-${b.id}`} className={`mt-1.5 inline-flex items-center gap-1 font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${g.cls}`}>{b.gate !== "ready" && <Lock className="w-2.5 h-2.5" />}{g.label}</div>}
      {!b.completed && b.produced_qty > 0 && <div className="mt-1.5 ml-1 inline-flex items-center gap-1"><span data-testid={`batch-partial-${b.id}`} className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-[#38BDF8]/20 text-[#38BDF8]">PARTIALLY COMPLETE · {fmt(b.produced_qty)} DONE</span>
        <button data-testid={`batch-undo-${b.id}`} disabled={busy} onClick={onUndo} title="Undo last registration" className="h-6 px-1.5 rounded-sm border border-[#334155] text-[#9CA3AF] hover:text-white flex items-center gap-1 font-mono text-[9px]"><Undo2 className="w-3 h-3" />UNDO</button></div>}
      {b.gate_missing?.length > 0 && <div className="text-[10px] text-[#FCA5A5] mt-1">Needs: {b.gate_missing.join(", ")}</div>}
      {b.material_delayed?.length > 0 && (
        <div data-testid={`batch-material-delayed-${b.id}`} className="mt-1.5 p-1.5 rounded-sm bg-[#F59E0B]/10 border border-[#F59E0B]/50 text-[10px] text-[#F59E0B]">
          <div className="font-mono font-bold">MATERIAL DELAYED</div>
          {b.material_delayed.map((m, i) => <div key={i}>{m.material}: {m.po_number} ETA {m.eta}</div>)}
        </div>
      )}
      {b.completed ? (
        <div className="flex items-center justify-between mt-1"><span className="font-mono text-lg font-bold text-[#10B981]">{fmt(b.qty)}×</span><span data-testid={`batch-done-${b.id}`} className="font-mono text-[10px] text-[#10B981] flex items-center gap-1"><Check className="w-3 h-3" />DONE
          <button data-testid={`batch-undo-${b.id}`} disabled={busy} onClick={onUndo} title="Undo this registration" className="ml-1 h-7 px-2 rounded-sm border border-[#334155] text-[#9CA3AF] hover:text-white flex items-center gap-1"><Undo2 className="w-3 h-3" />UNDO</button></span></div>
      ) : (<>
        <div className="flex items-center gap-1 mt-2">
          <button data-testid={`batch-minus-${b.id}`} onClick={() => setRun(runQty - 1)} className="h-9 w-9 rounded border border-[#334155] font-bold">−</button>
          <input data-testid={`batch-qty-${b.id}`} type="text" inputMode="numeric" value={draft} onChange={(e) => setDraft(e.target.value.replace(/[^\d.]/g, ""))}
            onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="no-spin h-9 w-14 text-center font-mono font-bold bg-[#090D16] border border-[#334155] rounded" />
          <button data-testid={`batch-plus-${b.id}`} onClick={() => setRun(runQty + 1)} className="h-9 w-9 rounded bg-[#F59E0B] text-[#090D16] font-bold">+</button>
          <div className="ml-auto flex">
            <button data-testid={`batch-left-${b.id}`} disabled={!canLeft} onClick={() => onMove(-1)} className="h-9 w-7 flex items-center justify-center text-[#9CA3AF] disabled:opacity-20"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid={`batch-right-${b.id}`} disabled={!canRight} onClick={() => onMove(1)} className="h-9 w-7 flex items-center justify-center text-[#9CA3AF] disabled:opacity-20"><ChevronRight className="w-4 h-4" /></button>
            <button data-testid={`batch-delete-${b.id}`} onClick={onDelete} className="h-9 w-7 flex items-center justify-center text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
        {short && <div data-testid={`batch-short-note-${b.id}`} className="mt-1 font-mono text-[10px] text-[#38BDF8]">{fmt(b.qty - runQty)} WILL REMAIN ON THIS CARD</div>}
        <button data-testid={`batch-complete-${b.id}`} disabled={busy || !canRun} onClick={() => onComplete(runQty)}
          className={`mt-2 w-full h-10 rounded font-mono text-[11px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-30 ${short ? "bg-[#38BDF8] text-[#090D16]" : b.tier === 2 ? "bg-[#6366F1] text-white" : "bg-[#10B981] text-[#090D16]"}`}>
          {canRun ? <><CheckCircle2 className="w-3.5 h-3.5" />{short ? "REGISTER PRODUCTION" : b.tier === 2 ? "COMPLETE JOB" : "COMPLETE BUILD"}</> : <><Lock className="w-3.5 h-3.5" />LOCKED</>}
        </button>
      </>)}
    </div>
  );
}

function Section({ icon: I, title, total, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between px-1 mb-1.5 font-mono text-[10px] tracking-[0.15em] text-[#9CA3AF]"><span className="flex items-center gap-1"><I className="w-3 h-3" />{title}</span><span>{fmt(total)}</span></div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Gauge({ day, capHours, mpu }) {
  const hrs = day.batches.reduce((a, b) => a + b.qty * mpu(b), 0) / 60;
  const cap = capHours;
  const pct = cap > 0 ? (hrs / cap) * 100 : hrs > 0 ? 999 : 0;
  const cls = pct > 100 ? "bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]" : pct >= 90 ? "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]" : "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/50";
  return (
    <div className="mt-2" data-testid={`day-capacity-${day.date}`}>
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm border font-mono text-[11px] font-bold ${cls}`}>
        <span data-testid={`day-hours-${day.date}`}>{hrs.toFixed(1)} / {cap.toFixed(1)} hrs</span>{pct > 100 && <span>· OVER</span>}
      </div>
      <div className="h-1 mt-1.5 bg-[#172033] rounded-sm overflow-hidden"><div className={`h-full ${pct > 100 ? "bg-[#EF4444]" : pct >= 90 ? "bg-[#F59E0B]" : "bg-[#10B981]"}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      {day.batches.some((b) => !mpu(b) && !b.machine_run_per_unit) && <div className="font-mono text-[10px] text-[#6B7280] mt-1" data-testid={`day-untimed-${day.date}`}>+{fmt(day.batches.filter((b) => !mpu(b) && !b.machine_run_per_unit).reduce((a, b) => a + b.qty, 0))} units with no time set</div>}
      {(day.machine_load || []).map((m) => {
        const p = m.capacity_minutes > 0 ? (m.run_minutes / m.capacity_minutes) * 100 : m.run_minutes > 0 ? 999 : 0;
        return (
          <div key={m.machine_id} className="mt-1.5" data-testid={`day-machine-${day.date}-${m.machine_id}`}>
            <div className={`font-mono text-[10px] ${p > 100 ? "text-[#EF4444]" : "text-[#A78BFA]"}`}>{m.machine_name}: {(m.run_minutes / 60).toFixed(1)} / {(m.capacity_minutes / 60).toFixed(1)} h{!m.active ? " · INACTIVE" : ""}</div>
            <div className="h-1 bg-[#172033] rounded-sm overflow-hidden"><div className={`h-full ${p > 100 ? "bg-[#EF4444]" : "bg-[#A78BFA]"}`} style={{ width: `${Math.min(100, p)}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function DayCard({ day, idx, days, products, update, onDrop, onComplete, onCompleteBatch, onUndoBatch, busy, capHours, mpu, onOrdering }) {
  const [adding, setAdding] = useState("");
  const move = (bi, dir) => {
    const t = idx + dir;
    if (!days[t]) return;
    update(idx, day.batches.filter((_, j) => j !== bi), t, [...days[t].batches, day.batches[bi]]);
  };
  const render = (b) => {
    const bi = day.batches.indexOf(b);
    return (
      <BatchCard key={b.id} b={b} busy={busy} canLeft={!!days[idx - 1]} canRight={!!days[idx + 1]}
        onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ from: idx, bi }))}
        onMove={(dir) => move(bi, dir)} onDelete={() => update(idx, day.batches.filter((_, j) => j !== bi))} onComplete={(q) => onCompleteBatch(b, false, q)} onUndo={() => onUndoBatch(b)} />
    );
  };
  const t2 = day.batches.filter((b) => b.tier === 2);
  const t3 = day.batches.filter((b) => b.tier === 3);
  const open = day.batches.filter((b) => !b.completed);
  return (
    <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, idx)} data-testid={`day-card-${day.date}`}
      className={`flex flex-col flex-1 min-w-[250px] shrink-0 self-start rounded-md border-2 bg-[#0F1623] ${BORDER[day.status]} ${!day.workday && !day.batches.length ? "opacity-50" : ""}`}>
      <div className="p-3 border-b border-[#1E293B] sticky top-0 z-10 bg-[#0F1623] rounded-t-md" data-testid={`day-header-${day.date}`}>
        <div className="flex items-center justify-between"><div className="font-display text-xl font-extrabold">{day.weekday}</div><span data-testid={`day-status-${day.date}`} className={`w-3 h-3 rounded-full ${DOT[day.status]}`} title={day.status} /></div>
        <div className="font-mono text-[11px] text-[#6B7280]">{day.date}{!day.workday ? " · OFF" : ""}</div>
      </div>
      <div className="px-3 pt-2 pb-3 border-b border-[#1E293B]" data-testid={`day-load-${day.date}`}>
        <div className="font-mono text-xs" data-testid={`day-total-${day.date}`}>{fmt(day.total)} UNITS</div>
        <Gauge day={day} capHours={capHours} mpu={mpu} />
      </div>
      <div className="p-2 space-y-3">
        {t2.length > 0 && <Section icon={Scissors} title="BULK CUT RUN · T2" total={day.t2_total} testId={`day-prep-${day.date}`}>{t2.map(render)}</Section>}
        {t3.length > 0 && <Section icon={Hammer} title="ASSEMBLY · T3" total={day.t3_total} testId={`day-assembly-${day.date}`}>{t3.map(render)}</Section>}
        <div className="flex gap-1">
          <select data-testid={`day-add-select-${day.date}`} value={adding} onChange={(e) => setAdding(e.target.value)} className="h-9 flex-1 min-w-0 px-2 text-xs bg-[#090D16] border border-[#334155] rounded">
            <option value="">+ Add batch…</option>
            {[2, 3].map((t) => <optgroup key={t} label={t === 2 ? "T2 Prep" : "T3 Assembly"}>{products.filter((p) => p.tier === t).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>)}
          </select>
          <button data-testid={`day-add-btn-${day.date}`} disabled={!adding} onClick={() => { const p = products.find((x) => x.id === adding); update(idx, [...day.batches, { id: crypto.randomUUID(), item_id: adding, qty: 1, tier: p.tier, name: p.name, completed: false }]); setAdding(""); }}
            className="h-9 w-9 flex items-center justify-center rounded border border-[#334155] disabled:opacity-30"><Plus className="w-4 h-4" /></button>
        </div>
      </div>
      {day.shortages.length + (day.missing_bom || []).length > 0 && (
        <div className="mx-2 mb-2 p-2 rounded bg-[#EF4444]/10 text-[11px] text-[#FCA5A5]" data-testid={`day-shortages-${day.date}`}>
          <div className="flex items-center gap-1 font-bold mb-1"><AlertTriangle className="w-3 h-3" />MATERIAL SHORTAGE</div>
          {day.shortages.map((s) => <div key={s.item_id}>{s.name}: short {fmt(s.short)} {s.unit}</div>)}
          {(day.missing_bom || []).map((n) => <div key={n}>{n}: no BOM</div>)}
        </div>
      )}
      <div className="p-2 pt-2 mt-auto border-t border-[#1E293B] space-y-2 empty:hidden" data-testid={`day-actions-${day.date}`}>
        {day.completed ? (
          <div className="h-12 flex items-center justify-center gap-2 text-[#10B981] font-mono text-xs font-bold" data-testid={`day-completed-${day.date}`}><CheckCircle2 className="w-4 h-4" />COMPLETED</div>
        ) : SHOW_COMPLETE_DAY && (
          <button data-testid={`day-complete-btn-${day.date}`} disabled={busy || !open.length} onClick={() => onComplete(day)}
            className="w-full h-12 rounded-md font-display font-extrabold text-sm disabled:opacity-30 active:scale-[0.98] bg-[#F59E0B] text-[#090D16]">COMPLETE DAY</button>
        )}
        {!day.completed && day.ordering_required && (
          <button data-testid={`day-ordering-btn-${day.date}`} onClick={() => onOrdering(day)}
            className="w-full h-12 rounded-md border-2 border-[#38BDF8] text-[#38BDF8] font-display font-extrabold text-sm flex items-center justify-center gap-2 hover:bg-[#38BDF8]/10 active:scale-[0.98]">
            <ShoppingCart className="w-4 h-4" />ORDERING REQUIRED
            {day.routine_orders > 0 && <span data-testid={`day-ordering-count-${day.date}`} className="font-mono text-[10px] bg-[#38BDF8] text-[#090D16] px-1.5 rounded-sm">{day.routine_orders} PAR</span>}
          </button>
        )}
      </div>
    </div>
  );
}

export default function WeeklySchedule() {
  const { items, boms, refreshAll } = useData();
  const [sched, setSched] = useState(null);
  const [hours, setHours] = useState([6, 6, 6, 6, 6, 0, 0]);
  const hoursT = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const saveT = useRef(null);
  const nav = useNavigate();
  const bomIds = new Set(boms.map((b) => b.product_id));
  const products = items.filter((i) => i.tier > 1 && bomIds.has(i.id));

  const load = async () => { const r = await api.get("/schedule"); setSched(r.data?.id ? r.data : null); };
  useEffect(() => { load(); api.get("/schedule/settings").then((r) => setHours(r.data.capacity_hours)); }, []);
  const bomsBy = Object.fromEntries(boms.map((b) => [b.product_id, b]));
  const mpu = (b) => b.minutes_per_unit ?? (bomsBy[b.item_id]?.resource_type === "machine" ? bomsBy[b.item_id]?.setup_labor_minutes : bomsBy[b.item_id]?.production_time_minutes) ?? 0;
  const dayIdx = (iso) => (new Date(`${iso}T00:00:00`).getDay() + 6) % 7;
  const eff = sched?.capacity_override ? sched.capacity_hours : hours;
  const setDayHours = (i, v) => {
    const next = eff.map((h, j) => (j === i ? Math.max(0, Math.min(24, Number(v) || 0)) : h));
    setHours(next);
    if (sched?.capacity_override) setSched({ ...sched, capacity_override: false });
    clearTimeout(hoursT.current);
    hoursT.current = setTimeout(() => api.put("/schedule/settings", { capacity_hours: next }).catch((e) => toast.error(errMsg(e))), 600);
  };

  const generate = async () => {
    if (sched && !window.confirm("Re-plan the week? Open batches will be replaced (completed batches are kept).")) return;
    setBusy(true);
    try { setSched((await api.post("/schedule/generate", { capacity_hours: hours })).data); toast.success("Week scheduled"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const applyRequired = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/schedule/generate", { apply_required: true })).data;
      setSched(r);
      toast.success(r.capacity_check?.deficit_hours > 0 ? `Hours raised; ${r.capacity_check.deficit_hours.toFixed(1)} h still short (24 h/day cap)` : "Hours raised for this week and schedule re-planned");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const persist = (days) => {
    clearTimeout(saveT.current);
    saveT.current = setTimeout(async () => {
      try { setSched((await api.put(`/schedule/${sched.id}`, { days: days.map((d) => ({ date: d.date, batches: d.batches.map(({ id, item_id, qty }) => ({ id, item_id, qty })) })) })).data); }
      catch (e) { toast.error(errMsg(e)); load(); }
    }, 500);
  };
  const update = (i, batches, j, batchesJ) => {
    const days = sched.days.map((d, k) => (k === i ? { ...d, batches } : k === j ? { ...d, batches: batchesJ } : d));
    setSched({ ...sched, days }); persist(days);
  };
  const onDrop = (e, to) => {
    e.preventDefault();
    const { from, bi } = JSON.parse(e.dataTransfer.getData("text/plain") || "{}");
    if (from === undefined || from === to) return;
    update(from, sched.days[from].batches.filter((_, k) => k !== bi), to, [...sched.days[to].batches, sched.days[from].batches[bi]]);
  };
  const run = async (url, label, retry, extra = {}) => {
    setBusy(true);
    try { setSched((await api.post(url, { force: !!retry.force, ...extra })).data); toast.success(label); refreshAll(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gated) toast.error(`${d.message}: ${d.shortages.map((x) => x.name).join(", ")}`);
      else if (e?.response?.status === 409) toast.error(`${d.message ? `${d.message}: ` : "Insufficient: "}${d.shortages.map((x) => x.name).join(", ")}`, { action: { label: d.message?.startsWith("Some") ? "Undo anyway" : "Complete anyway", onClick: () => retry.again() }, duration: 10000 });
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  const completeBatch = (b, force = false, qty = b.qty) => run(`/schedule/${sched.id}/batches/${b.id}/complete`,
    qty < b.qty ? `Registered ${fmt(qty)} × ${b.name} · ${fmt(b.qty - qty)} still to make` : b.tier === 2 ? `Cut complete: ${b.name} — assembly unlocked` : `Built ${fmt(qty)} × ${b.name}`,
    { force, again: () => completeBatch(b, true, qty) }, { qty });
  const [undoing, setUndoing] = useState(null);
  const undoBatch = (b, force = false) => {
    if (!force) return setUndoing(b);
    run(`/schedule/${sched.id}/batches/${b.id}/undo`, `Undone: ${b.name}`, { force, again: () => undoBatch(b, true) });
  };
  const completeDay = (day, force = false) => {
    if (!force && !window.confirm(`Register all open batches for ${day.weekday} ${day.date}? Prep runs first, then assembly.`)) return;
    run(`/schedule/${sched.id}/days/${day.date}/complete`, `${day.weekday} production registered`, { force, again: () => completeDay(day, true) });
  };
  const sum = (k) => (sched ? sched.days.reduce((a, d) => a + (d[k] || 0), 0) : 0);
  const weekHrs = sched ? sched.days.reduce((a, d) => a + d.batches.reduce((x, b) => x + b.qty * mpu(b), 0), 0) / 60 : 0;

  const confirmUndo = () => {
    const b = undoing;
    setUndoing(null);
    run(`/schedule/${sched.id}/batches/${b.id}/undo`, `Undone: ${b.name}`, { force: false, again: () => undoBatch(b, true) });
  };

  return (
    <div>
      <UndoBatchDialog batch={undoing} onCancel={() => setUndoing(null)} onConfirm={confirmUndo} />
      <PageHeader kicker="// UNIFIED 7-DAY TIMELINE" title="Schedule Week">
        <div className="flex gap-1" data-testid="workday-toggles">
          {WD.map((w, i) => (
            <div key={w} className={`w-14 rounded-md border flex flex-col items-center overflow-hidden ${eff[i] > 0 ? "border-white" : "border-[#334155]"}`}>
              <button data-testid={`workday-${w}`} onClick={() => setDayHours(i, eff[i] > 0 ? 0 : 6)} className={`w-full h-7 font-mono text-[11px] font-bold ${eff[i] > 0 ? "bg-white text-[#090D16]" : "text-[#6B7280]"}`}>{w}</button>
              <input data-testid={`capacity-hours-${w}`} type="number" step="0.5" min="0" max="24" value={eff[i]} onChange={(e) => setDayHours(i, e.target.value)} className="no-spin w-full h-8 text-center font-mono text-sm bg-[#090D16] focus:outline-none" title="Capacity hours" />
            </div>
          ))}
        </div>
        <button data-testid="schedule-generate-btn" disabled={busy} onClick={generate} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2 disabled:opacity-40"><Wand2 className="w-4 h-4" />{sched ? "Re-plan Week" : "Auto-Schedule"}</button>
        <Schedule90Button onDone={load} />
      </PageHeader>
      {!sched ? (
        <Panel className="p-8 text-[#9CA3AF]" data-testid="schedule-empty">No schedule yet. Set daily capacity hours (0 = inactive day) and tap Auto-Schedule. Tier 2 prep is consolidated into bulk cut runs early in the week; Tier 3 assembly follows once its parts are ready.</Panel>
      ) : (<>
        <div className="flex flex-wrap items-center gap-6 mb-4 font-mono text-xs text-[#9CA3AF]">
          <span data-testid="schedule-prep-total">PREP <span className="text-[#A5B4FC] text-base">{fmt(sum("t2_total"))}</span></span>
          <span data-testid="schedule-week-total">ASSEMBLY <span className="text-white text-base">{fmt(sum("t3_total"))}</span></span>
          <span data-testid="schedule-week-hours">LOAD <span className="text-white text-base">{weekHrs.toFixed(1)}</span> / {eff.reduce((a, b) => a + b, 0).toFixed(1)} HRS{sched.capacity_override && <span data-testid="schedule-week-override" className="ml-2 text-[#38BDF8]">· WEEK OVERRIDE</span>}</span>
          <CapacityDeficit check={sched.capacity_check} backlog={sched.backlog || []} activeDays={eff.filter((h) => h > 0).length} onApply={applyRequired} busy={busy} />
          <span>STARTS {sched.start_date}</span>
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />READY</span>
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />AWAITING PREP</span>
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />SHORTAGE / BLOCKED</span>
        </div>
        <div className="flex justify-end mb-2">
          <button data-testid="schedule-show-empty-btn" onClick={() => setShowEmpty(!showEmpty)} className="h-9 px-3 rounded-md border border-[#334155] text-xs font-mono text-[#9CA3AF]">
            {showEmpty ? "HIDE EMPTY DAYS" : `SHOW EMPTY DAYS (${sched.days.filter((d) => !d.batches.length).length})`}</button>
        </div>
        <div className="flex gap-3 overflow-auto max-h-[calc(100vh-180px)] pb-8 mb-4 items-start" data-testid="schedule-calendar">
          {sched.days.map((d, i) => (showEmpty || d.batches.length > 0) && <DayCard key={d.date} day={d} idx={i} days={sched.days} products={products} update={update} onDrop={onDrop} onComplete={completeDay} onCompleteBatch={completeBatch} onUndoBatch={undoBatch} busy={busy} capHours={eff[dayIdx(d.date)]} mpu={mpu} onOrdering={(day) => nav(`/ordering/suggested?schedule=${sched.id}&date=${day.date}`)} />)}
        </div>
        {(sched.chain_warnings || []).length > 0 && (
          <Panel className="p-4 mb-4 border-[#EF4444]/60" data-testid="schedule-chain-warnings">
            <div className="font-mono text-[10px] tracking-[0.2em] text-[#EF4444] mb-1">STAGE CHAIN WARNING</div>
            {sched.chain_warnings.map((w, i) => <div key={i} className="text-sm text-[#FCA5A5]">{w.message}</div>)}
          </Panel>
        )}
        {(sched.backlog || []).length > 0 && (
          <Panel className="p-5 mt-2 border-[#F59E0B]/50" data-testid="schedule-backlog">
            <div className="font-mono text-xs tracking-[0.2em] text-[#F59E0B] mb-3">BACKLOG / NEXT WEEK · OVER WEEKLY CAPACITY</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {sched.backlog.map((b) => (
                <div key={`${b.item_id}-${b.tier}`} data-testid={`backlog-${b.sku}`} className="p-3 rounded-md border border-[#1E293B] text-sm">
                  <div className="font-mono text-[10px] text-[#6B7280]">{b.tier === 2 ? "T2 PREP" : "T3 ASSEMBLY"}</div>
                  <div className="font-semibold">{b.name}</div>
                  <div className="font-mono text-xs text-[#9CA3AF]">{fmt(b.qty)} units · {(b.minutes / 60).toFixed(1)} hrs</div>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </>)}
    </div>
  );
}
